<script setup lang="ts">
/**
 * 開站精靈（`/provision?host=<id>`；admin 以上）——批C2 的「動得了」那一半。
 *
 * 五步：①客戶 ②主機 ③網域 ④授權 ⑤確認總表 → 送出 → 導向該 job 的進度頁。
 *
 * 三個實作要點：
 * 1. **preflight 是唯一真相**：第三步 slug 打字（去抖 500ms）與第五步總表都呼同一個
 *    `POST /hosts/{id}/sites/preflight`——不在前端自己複製一份「slug 黑名單／LE 額度／
 *    資源餘裕」的判斷（那必然跟後端走鐘）。前端只做「明顯格式錯」的即時提示。
 * 2. **ModulePicker／QuotaEditor 的 `initFrom` 只在掛載時跑**：套用套裝後必須換 `:key`
 *    重掛才會反映到 UI（沿用 `CustomersView` 的招數）。
 * 3. **開站不代建帳號**（D10）：完成後把 `setup_url` 交給客戶自己跑初始設定。
 * 4. **依產品的差異全讀代管設定**（批141）：網域 zone、出貨 channel、可選的站台選項
 *    （pgweb／反向隧道）、初始設定網址（含一次性權杖的只顯示這一次）——本頁不寫任何
 *    產品字面，也不寫死網域。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { vEnter } from '@/lib/enterKey'
import { featuresOf, hasFeature, rawProfileOf } from '@/lib/hostingProfile'
import type { FeatureKey } from '@/lib/hostingProfile'
import { hostedContentBlocked, hostedContentHint, hostedContentReason } from '@/lib/hostedContent'
import { defaultModulesOf, issuablePlans, planByKey, termLabel } from '@/lib/products'
import {
  SITE_SECRET_ERROR_MESSAGES,
  declaredSecretFields,
  filledSecrets,
  provisionSecretBlocker,
} from '@/lib/siteSecrets'
import { MINUTES_PER_DAY, splitMinutes } from '@/lib/terms'
import ModulePicker from '@/components/ModulePicker.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import SiteSecretInputs from '@/components/SiteSecretInputs.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiMeter from '@/components/UiMeter.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { ManagedHost, ProvisionIn, ProvisionPreflight, ProvisionResult } from '@/api/hosting'
import * as ls from '@/api/ls'
import * as reseller from '@/api/reseller'
import { hostStatusLabel } from '@/lib/fleet'
import { buildQuotaRows, planLabel, quotaSummaryText } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

const STEPS = [
  { n: 1, label: '客戶' },
  { n: 2, label: '主機' },
  { n: 3, label: '網域' },
  { n: 4, label: '授權' },
  { n: 5, label: '確認' },
]
const step = ref(1)
/** 走過的最遠一步——步驟指示器只允許往回點已經走過的。 */
const furthest = ref(1)

// --- ① 客戶 ---
const custMode = ref<'existing' | 'new'>('existing')
const customerId = ref('')
const newCustName = ref('')
const newCustDomain = ref('')
const custBusy = ref(false)
const custError = ref('')

// --- ② 主機 ---
const hosts = ref<ManagedHost[]>([])
const hostId = ref(String(route.query.host ?? ''))

// --- ③ 網域／站台選項 ---
const slug = ref('')
const domainMode = ref<'ours' | 'byo'>('ours')
const byoDomain = ref('')
// ★★ 批112（§G）：**預設開**。代管站的定義就是「掛在我方入口後面」，而反向隧道正是
//    那個入口；預設關會讓每個新站都要有人記得勾，忘了就是「開好了但外部通路 webhook
//    進不來」。後端 schema 的預設也是 `True`，但這支精靈**無條件送明確值**
//    （`buildPayload` 的 `tunnel_enabled: tunnelEnabled.value`），所以後端改了預設而這裡
//    沒改＝§G 等於沒做——兩條 UI 開站路徑（我方精靈＋代理商精靈共用本元件）都還是關的。
//    ⚠️ 帶這個鍵要求 **agent ≥ 0.2.8**（舊 agent 對未知參數一律整個 job 失敗）。
//    2026-09-16 實查 ls-dev／ls-prod：在役主機各 1 台、皆 0.2.11。
const tunnelEnabled = ref(true)
const pgwebEnabled = ref(false)

// --- ④ 授權 ---
/** 開站授權的期限（**總分鐘**，批108）。 */
const termTotal = ref(365 * MINUTES_PER_DAY)
const plan = ref('')
/**
 * 要開哪個產品的站（批99-A）——**候選＝我可以開的 ∩ 這台 agent 裝得起的**：
 *
 * - `auth.hostingProductKeys`＝後端算好的「支援代管的產品 ∩ 我的有效範圖」；
 * - `selectedHost.agent_products`＝這台 agent 自報的能力（舊 agent 已由後端回成 `["iplusone-cs"]`）。
 *
 * 交集只有一個（今天的常態）→ 唯讀文字，不給選；多於一個才出現 `<select>`。
 * ★ 前端**不自己判斷哪個產品能代管**（那是 `products.options.supports_hosting`，
 *   前端拿不到完整資料）——一律用 `/me` 給的那份清單。真正的閘在後端三重檢查。
 */
const productKey = ref('')
/** 產品的中文名（憑證設計還沒載回來時退回原始鍵，不留空白）。 */
function productLabel(key: string): string {
  return store.productByKey(key)?.name || key
}
const spec = computed(() => store.productByKey(productKey.value))
/**
 * 套裝 chip 的清單＝可販售 ∪ **試用模板**（`issuablePlans`），**我方與代理商都一樣**。
 *
 * ★ 批112 修正：原本我方視角只列 `sellablePlans`（批99-B「我方視角維持原樣」），結果我方
 *   精靈**開不出試用站**——`ProvisionIn` 也沒有 `trial` 旗標，開出來的證 `payload.trial`
 *   永遠是 false ⇒ 到期不會自動停站、批112 的共用 AI 額度（判準之一就是 `payload.trial`）
 *   永遠吃不到。主人 09-17 抓到「開立新代管站時無法選擇試用套裝」。
 *   按試用 chip ＝ `plan=trial`＋`trial=true`＋期限換成代管的 `hosted_trial_term_days`（30 天）。
 */
// 批155：內部套裝只有營運主管以上列得出來（代理商恆不列）。
const plans = computed(() => issuablePlans(spec.value, { internal: auth.isManagerOrAbove }))
/** 這張證是不是試用（按試用套裝時為 true；後端也會依套裝 kind 自己推導，這裡是明確送）。 */
const trial = ref(false)
/**
 * 這個套裝**這個角色能不能直接拿來開站**——依角色＋偏離判斷（批99-B 立、批151 裁示 50 改）。
 *
 * - **代理商**：後端閘是「內容必等於某套裝模板，且該套裝 `activation_policy === 'reseller'`」，
 *   否則 422 `activation_policy_required` → 其餘套裝灰掉並說明去處。
 * - **我方**：**開站內容閘**（裁示 50）不看套裝本身的 `activation_policy`——照任何套裝的內容
 *   開站 admin 都可以，所以套裝 chip 一律可按；要升到營運主管的是「偏離套裝」與「沒選套裝」，
 *   那是**內容**層的判斷，見下面的 `contentReason`／`contentBlocked`（admin 偏離時提前提示並
 *   擋住下一步，不要按了才 403）。
 */
function planDirectOk(policy: string | null | undefined): boolean {
  return !auth.isReseller || policy === 'reseller'
}
/**
 * 「客製」chip 這個角色能不能選：代理商一律不行（走申請流程）；我方要營運主管以上
 * （沒選套裝＝客製內容＝開站內容閘的 `no_plan`）。
 */
const customPlanOk = computed(() => !auth.isReseller && !hostedContentBlocked(auth.role, 'no_plan'))
// 全模組起手：鍵集合來自**該產品的憑證設計**（批93 之前是 `MODULE_LABELS`，更早是寫死
// 的清單——批81 加 `api` 時漏同步，開站精靈的預設就少一個模組）。
const modules = ref<Record<string, string>>({})
/**
 * 數量配額（批114 起是**唯一**的數量入口；獨立的數字欄已拔除）。
 * `quotasValid` 是 `QuotaEditor` 回報的必填檢查——起手 true（訊號還沒到之前不該擋人）。
 */
const quotas = ref<Record<string, number | null> | null>(null)
const quotasValid = ref(true)
const pickerKey = ref(0)
const quotaKey = ref(0)

/**
 * **開站內容閘**（批151 裁示 50）的即時判斷——我方視角才算（代理商有自己的閘）。
 * `null`＝照套裝內容；`no_plan`／`custom_content`＝要營運主管。判準是「真的會簽的內容」
 * （`lib/hostedContent.ts`，與後端 `hosted_content_reason` 逐行鏡像）。
 */
const contentReason = computed(() => {
  if (auth.isReseller) return null
  return hostedContentReason(spec.value, planByKey(spec.value, plan.value || null), {
    modules: modules.value,
    quotas: quotas.value,
    term_days: splitMinutes(termTotal.value)[0],
    term_minutes: splitMinutes(termTotal.value)[1],
    trial: trial.value,
  })
})
/** admin 偏離／客製＝送出必 403 → 提前擋在第四步。營運主管以上只提示、不擋。 */
const contentBlocked = computed(() => hostedContentBlocked(auth.role, contentReason.value))
const contentHint = computed(() => hostedContentHint(contentReason.value, contentBlocked.value))

// --- ④ 站台機密（批151 C6；營運主管以上、產品有宣告才出現）---
/** 值只活在這裡，**只在最後送出時**帶進 body（preflight 不帶）；換產品或開站成功即清空。 */
const secretValues = ref<Record<string, string>>({})

// --- preflight ---
const pre = ref<ProvisionPreflight | null>(null)
const preBusy = ref(false)
const preError = ref('')

const submitting = ref(false)
const result = ref<ProvisionResult | null>(null)
const resultOpen = ref(false)
const copied = ref(false)
/** 初始設定網址**複製過至少一次**（含一次性權杖的網址，關窗前要確認有帶走）。 */
const copiedOnce = ref(false)

/**
 * 這個產品的代管功能旗標（批141）——決定第三步要不要出現 pgweb／反向隧道兩格。
 *
 * 來源依序：這個產品**最近一次 preflight** 回來的 `profile.features`（權威、兩個 realm 都有）
 * → 產品憑證設計裡的代管設定（我方視角才載得到產品清單）。
 * - `undefined`＝還不知道（先不畫那兩格，免得畫了又消失）；
 * - `null`＝這個產品沒有有效的代管設定（開不了站，preflight 的 `issues` 會講原因）。
 */
const lastPre = ref<ProvisionPreflight | null>(null)
watch(pre, (p) => {
  if (p) lastPre.value = p
})
const profileFeatures = computed<Record<string, boolean> | null | undefined>(() => {
  const p = lastPre.value
  if (p?.profile && (p.product ?? productKey.value) === productKey.value) return p.profile.features ?? null
  // 批151 R1-16：代理商拿到的產品投影沒有代管設定（白名單），從那裡讀會誤判成「沒有有效的代管設定」
  // ——代理商一律等 preflight 才知道。
  if (spec.value && !auth.isReseller) return featuresOf(spec.value.options)
  return undefined
})
function featureOn(k: FeatureKey): boolean {
  return hasFeature(profileFeatures.value, k)
}

/** 最近一次、**同產品**的 preflight（換了產品就不能沿用上一個產品的判斷）。 */
const samePre = computed(() => {
  const p = lastPre.value
  return p && (p.product ?? productKey.value) === productKey.value ? p : null
})
/**
 * 這個產品宣告的站台機密欄位：preflight 的 `site_secret_fields`（權威）→ 產品代管設定裡的
 * `site_secrets`（我方視角載得到憑證設計時的退路）。
 */
const secretFields = computed(
  () => samePre.value?.site_secret_fields ?? declaredSecretFields(rawProfileOf(spec.value?.options)),
)
/** 只有營運主管以上看得到（後端對低於營運主管帶值一律 403）；代理商 realm 結構上收不到。 */
const showSecrets = computed(() => !auth.isReseller && auth.isManagerOrAbove && secretFields.value.length > 0)
/** 這台主機現在帶不帶得了（`null`＝可以）：判準在 preflight 回的兩個旗標。 */
const secretBlocker = computed(() => {
  const p = samePre.value
  return provisionSecretBlocker(p && p.site_secret_fields ? p : null)
})
/** 真的要送的站台機密（不能帶、或一欄都沒填＝`null`，整個鍵不送）。 */
const secretsToSend = computed(() =>
  showSecrets.value && !secretBlocker.value ? filledSecrets(secretValues.value) : null,
)
/** 有填的欄位長度都合規（不合規就擋在第四步——值的完整規則仍由後端驗）。 */
const secretsValid = computed(() =>
  Object.values(secretsToSend.value ?? {}).every((v) => v.length >= 8 && v.length <= 512),
)

/**
 * 這個產品的站台 zone 用哪個 DNS provider（批141-R）——與 `profileFeatures` 同一個來源
 * （最近一次、同產品的 preflight）。`undefined`＝還不知道。
 *
 * ★ `manual`＝這個 zone 沒有接 DNS API（全域設定就是 manual，或產品的代管設定自訂了 zone
 *   卻沒有對應的 Cloud DNS managed zone，後端 `dns.provider_for` 會退回 manual）：
 *   開站**不會自動建 A 記錄**、slug 也**沒有比對 zone 的既有記錄**（manual 查不到）。
 *   這時候不可以再寫「自動建 A 記錄」「DNS zone 都查過了」。
 */
const dnsProvider = computed<string | undefined>(() => {
  const p = pre.value ?? lastPre.value
  if (p && (p.product ?? productKey.value) === productKey.value) return p.dns_provider
  return undefined
})
const dnsManual = computed(() => dnsProvider.value === 'manual')
/**
 * 我方子網域開站時**會不會真的自動建 A 記錄**（批141-S）。`undefined`＝還不知道。
 *
 * provider 不是 manual 還不夠：主機沒有對外 IP 時後端同樣不建（preflight 的
 * `domain_check.will_create_dns`＝`public_ip && provider != manual`）。優先採信最近一次
 * **我方子網域模式**的 preflight；切到自帶網域時那份是 BYO 的（恆 false），改用同一條判準自己算。
 */
const willCreateDns = computed<boolean | undefined>(() => {
  const p = pre.value ?? lastPre.value
  if (p && (p.product ?? productKey.value) === productKey.value && p.domain_check?.mode === 'ours') {
    return p.domain_check.will_create_dns
  }
  if (dnsProvider.value === undefined) return undefined
  return !dnsManual.value && !!selectedHost.value?.public_ip
})
// 產品不支援的選項一律關掉——不送 true 給不支援的產品（pgweb 會被 422、隧道會被略過並附註）。
// 從「不支援」換到「支援」的產品時，反向隧道回到預設的開（批112 §G：代管站預設走隧道）。
watch(
  profileFeatures,
  (f, old) => {
    if (f === undefined) return
    if (!hasFeature(f, 'pgweb')) pgwebEnabled.value = false
    if (!hasFeature(f, 'tunnel')) tunnelEnabled.value = false
    else if (old !== undefined && !hasFeature(old, 'tunnel')) tunnelEnabled.value = true
  },
  { immediate: true },
)

/**
 * 開站結果的初始設定網址**帶著一次性權杖**嗎（批141：只出現在這一次回應）。
 * 批141 M 包：一律照後端回的 `setup_url_one_time`——代理商視角讀不到產品的代管設定，
 * 以前靠「網址裡有沒有一段權杖長度的亂碼」猜，兩個視角的判斷會不一致。
 */
const setupOneTime = computed(() => Boolean(result.value?.setup_url && result.value.setup_url_one_time))
/**
 * 出貨 channel 看起來是不是測試用的（名稱是 `dev` 或以 `-dev` 結尾）。
 * 裁示 48（LS 1.0.62）：代管設定只存**這台**授權中心的單一 channel，不再有「測試／正式」
 * 兩格可比對，所以只看名稱——純提示，不影響開站。
 */
function isDevChannel(channel: string | null | undefined): boolean {
  if (!channel) return false
  return /(^|-)dev$/.test(channel)
}

/**
 * 開站失敗的錯誤碼 → 給操作者看的一句話（批99-A／99-B）。
 *
 * ★ 後端的 `message` 多半已經是中文，這份字典只在「後端訊息太技術／太短」時接手；
 *   **查得到碼就用字典**（措辭可控、含下一步），查不到才退回 `message`。
 *   漏收的碼一律原樣顯示，不會吞掉資訊。
 */
const ERROR_MESSAGES: Record<string, string> = {
  // 批99-B：代理商直開只准「內容＝某個套裝模板，且該套裝 activation_policy 為 reseller」。
  activation_policy_required:
    '這份內容不在代理商可免審核發的套裝範圍，請改走授權申請流程——我方審核核發後會自動派到站上。',
  // 批99-B：代理商主機上只能為該代理商自己的客戶開站（我方 admin 也一樣受這條限制）。
  host_reseller_mismatch: '這位客戶不屬於這台主機的代理商，換一台主機或改選該代理商的客戶。',
  // 批151 C6：開站帶站台機密的錯誤碼（`role_required` 不在這份表裡：它也可能來自開站內容閘，
  // 兩道閘的後端訊息本身就寫清楚是哪一道，照原文顯示）。
  ...SITE_SECRET_ERROR_MESSAGES,
  // 批99-A 的三重閘。
  product_not_hostable: '這個產品目前沒有開放由我方代管。',
  host_product_unsupported: '這台主機的 agent 不支援這個產品（可能要先升級 agent）。',
  product_out_of_scope: '這個產品不在您的產品範圍內。',
  hosting_unavailable: '您的產品範圍內沒有開放代管的產品。',
  // 批141：產品開放代管卻沒有有效的代管設定（fail-closed）。
  hosting_profile_missing: '這個產品還沒有有效的代管設定，暫時開不了站（請營運主管到〈產品〉的「代管設定」頁籤補齊）。',
  pgweb_unsupported: '這個產品的站台不支援資料庫檢視（pgweb），請取消勾選。',
}

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return ERROR_MESSAGES[d] ?? d
  // provision 失敗時後端回 `{code, message}`
  const code = (d as { code?: unknown }).code
  if (typeof code === 'string' && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]
  const m = (d as { message?: unknown }).message
  return typeof m === 'string' ? m : JSON.stringify(d)
}

void store.ensureCustomers().catch(() => {})
void store
  .ensureProducts()
  .then(() => {
    // spec 到手才知道有哪些模組鍵；帶入後 bump `:key` 讓 ModulePicker 重掛。
    modules.value = defaultModulesOf(spec.value)
    pickerKey.value++
    quotaKey.value++
  })
  .catch(() => {})
void hosting
  .listManagedHosts()
  .then((rows) => { hosts.value = rows })
  .catch(() => {})

const selectedCustomer = computed(() => store.customers.find((c) => c.id === customerId.value) ?? null)
const selectedHost = computed(() => hosts.value.find((h) => h.id === hostId.value) ?? null)

/**
 * 這次開站可選的產品＝`auth.hostingProductKeys` ∩ 這台 agent 自報的 `agent_products`。
 * 還沒選主機時先給我可開的全集（第二步的主機列表要靠它算阻擋理由）。
 */
const productCandidates = computed<string[]>(() => {
  const mine = auth.hostingProductKeys
  const host = selectedHost.value
  if (!host) return [...mine]
  // ★ 批114 裁示 ㉟：`agent_products` 為 null＝**尚未回報**（舊 agent 的相容規則已拿掉）
  //   → 支援集合視為空，這台開不了站。
  const agent = new Set(host.agent_products ?? [])
  return mine.filter((k) => agent.has(k))
})

/** 這台主機的 agent 還沒回報過支援的產品（批114 裁示 ㉟）——與「一個都不支援」分開講。 */
const hostNeedsAgentUpgrade = computed(
  () => !!selectedHost.value && selectedHost.value.agent_products == null,
)

/** 候選變動（`/me` 載回來、換主機）時把產品修正成合法值；只有一個時等於「自動選好」。 */
watch(
  productCandidates,
  (list) => {
    if (!productKey.value || !list.includes(productKey.value)) productKey.value = list[0] ?? ''
  },
  { immediate: true },
)

/**
 * 換產品＝整包重帶預設模組／配額。
 * 模組鍵、配額鍵、套裝都是**逐產品**的，留著上一個產品的內容會送出對方不存在的鍵；
 * `ModulePicker`／`QuotaEditor` 的 `initFrom` 只在掛載時跑 → 必須 bump `:key` 重掛。
 */
watch(productKey, () => {
  plan.value = ''
  modules.value = defaultModulesOf(spec.value)
  quotas.value = null
  // 站台機密的欄位是逐產品宣告的：上一個產品填的值不能帶到另一個產品。
  secretValues.value = {}
  pickerKey.value++
  quotaKey.value++
})

/**
 * 這台 agent 對「我可以開的產品」一個都不支援（批99-A）。
 *
 * ★ 判準刻意是 `hostingProductKeys` 的**全集**、不是當下選定的 `productKey`：
 *   選主機時產品還會跟著主機自動收斂（見上面的 watch），拿選定值去擋會把
 *   「只能裝另一個產品」的主機也變成不可選 → 使用者永遠切不過去（死鎖）。
 */
function agentProductBlocker(h: ManagedHost): string | null {
  // ★ 批114 裁示 ㉟：null＝這台 agent 從沒回報過支援的產品（舊版 agent）。那不是
  //   「不支援」而是「不知道」——訊息要指路去升級 agent，不然操作者只會一直換主機試。
  if (h.agent_products == null) return 'agent 尚未回報支援的產品，請升級 agent'
  const agent = new Set(h.agent_products)
  const mine = auth.hostingProductKeys
  if (mine.some((k) => agent.has(k))) return null
  const names = mine.map(productLabel).join('、')
  return names ? `這台 agent 不支援「${names}」` : '您目前沒有可以開站的產品'
}

/**
 * 歸屬不符（批99-B）：代理商的主機上只能為**該代理商的客戶**開站，我方 admin 也一樣
 * （後端 422 `host_reseller_mismatch`）。
 *
 * ★ 只在我方視角算得出來——代理商看得到的主機與客戶本來就都是自己的（後端過濾），
 *   而且他的客戶列投影裡的 `reseller_id` 是本地補上的，拿來比對只是繞圈子。
 */
function ownerBlocker(h: ManagedHost): string | null {
  if (auth.isReseller || !h.reseller_id) return null
  const cust = selectedCustomer.value
  if (!cust) return null
  if ((cust.reseller_id ?? null) === h.reseller_id) return null
  return `這台是代理商「${h.reseller_name ?? '—'}」的主機，只能開給該代理商的客戶`
}

/** 主機不能用的理由（空陣列＝可用）；與後端 preflight 的 `issues` 同一組判準。 */
function hostBlockers(h: ManagedHost): string[] {
  const out: string[] = []
  if (h.status !== 'active') out.push(`主機狀態為「${hostStatusLabel(h.status)}」（需要「運行中」）`)
  if (!h.agent_enrolled) out.push('agent 尚未完成註冊')
  const prod = agentProductBlocker(h)
  if (prod) out.push(prod)
  const owner = ownerBlocker(h)
  if (owner) out.push(owner)
  if (h.exclusive && h.exclusive_customer_id && h.exclusive_customer_id !== customerId.value) {
    out.push(`已獨佔給「${h.exclusive_customer_name ?? '其他客戶'}」`)
  }
  // 批141（主人裁示）：**獨佔主機＝整台只開一站**——上面已經有站（含開站失敗留下的殘列）
  // 就不能再開，即使是同一位客戶。後端 preflight 同一條 issue、開站回 409 `exclusive_violation`。
  if (h.exclusive && h.sites_count > 0) {
    out.push('獨佔主機已有站台（獨佔主機整台只開一站）')
  }
  if (h.max_sites != null && h.sites_count >= h.max_sites) {
    out.push(`站數已達上限（${h.sites_count}/${h.max_sites}）`)
  }
  return out
}
const hostRows = computed(() =>
  hosts.value.map((h) => ({ host: h, blockers: hostBlockers(h) })),
)

// --- 表單驗證（只做「明顯格式錯」；業務規則一律靠 preflight）---
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,23}$/
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/
const slugOk = computed(() => SLUG_RE.test(slug.value.trim().toLowerCase()))
const byoOk = computed(() => DOMAIN_RE.test(byoDomain.value.trim().toLowerCase()))

/** 自帶網域的**阻斷級**問題（格式錯／已被別站登記）。DNS 還沒指過來不算阻斷
 *  ——BYO 本來就常是「先開站、客戶之後才改 DNS」，開站流程會先跳過 TLS。 */
const domainIssue = computed(
  () =>
    domainMode.value === 'byo' &&
    !!pre.value?.domain_check &&
    (pre.value.domain_check.in_use === true || pre.value.domain_check.format_ok === false),
)

const stepOk = computed<Record<number, boolean>>(() => ({
  1: !!customerId.value,
  2: !!selectedHost.value && hostBlockers(selectedHost.value).length === 0,
  3:
    slugOk.value &&
    (domainMode.value === 'ours' || byoOk.value) &&
    !pre.value?.slug_error &&
    !domainIssue.value,
  // 批114：數量只剩配額一處——必填檢查由 `QuotaEditor` 依憑證設計的旗標回報。
  // 批151：admin 的內容偏離套裝／客製＝送出必 403（開站內容閘）→ 提前擋在這一步。
  4:
    !!productKey.value &&
    quotasValid.value &&
    Number(termTotal.value) >= 1 &&
    !contentBlocked.value &&
    secretsValid.value,
  5: !!pre.value?.ok,
}))
const canNext = computed(() => stepOk.value[step.value] === true)

function buildBody(): ProvisionIn {
  return {
    customer_id: customerId.value,
    // 缺席時後端會退回 `default_product_key`；我們一律明寫（精靈上顯示的是哪個就送哪個）。
    product: productKey.value || undefined,
    slug: slug.value.trim().toLowerCase(),
    term_days: splitMinutes(termTotal.value)[0],
    term_minutes: splitMinutes(termTotal.value)[1],
    modules: modules.value,
    // 批114：數量只送這一包（含值為 `null` 的「無上限」）。
    quotas: quotas.value,
    plan: plan.value || null,
    trial: trial.value,
    domain_mode: domainMode.value,
    byo_domain: domainMode.value === 'byo' ? byoDomain.value.trim().toLowerCase() || null : null,
    tunnel_enabled: tunnelEnabled.value,
    pgweb_enabled: pgwebEnabled.value,
  }
}

async function runPreflight() {
  if (!customerId.value || !hostId.value || !slug.value.trim()) return
  preBusy.value = true
  try {
    pre.value = await hosting.preflightHostSite(hostId.value, buildBody())
    preError.value = ''
  } catch (e) {
    pre.value = null
    preError.value = errText(e, '開站前檢查失敗')
  } finally {
    preBusy.value = false
  }
}

// slug／網域改動 → 去抖 500ms 再打 preflight（打字每個字都打一次會刷爆 DNS zone 查詢）。
let preTimer: number | null = null
function schedulePreflight() {
  if (preTimer !== null) window.clearTimeout(preTimer)
  preTimer = window.setTimeout(() => {
    preTimer = null
    void runPreflight()
  }, 500)
}
watch([slug, domainMode, byoDomain], () => {
  pre.value = null
  if (step.value === 3 && slugOk.value) schedulePreflight()
})
// 離開頁面時清掉待跑的去抖計時器（否則換頁後還會打一次 preflight）。
onBeforeUnmount(() => {
  if (preTimer !== null) window.clearTimeout(preTimer)
  preTimer = null
})

function goto(n: number) {
  if (n > furthest.value) return
  step.value = n
}
async function next() {
  if (!canNext.value) return
  step.value += 1
  furthest.value = Math.max(furthest.value, step.value)
  // 進第三步先算一次（slug 已填時）；進第五步一定重算（授權欄位也進 body）。
  if (step.value === 3 && slugOk.value) await runPreflight()
  if (step.value === 5) await runPreflight()
}
function prev() {
  if (step.value > 1) step.value -= 1
}

// --- ① 建立新客戶 ---
async function createCustomer() {
  const name = newCustName.value.trim()
  if (!name) return
  custBusy.value = true
  try {
    // 批99-B：代理商打 `POST /api/admin/customers` 是 403 → 改打自己的 realm，
    // 後端強制把 `reseller_id` 寫成他自己（body 根本不收這一欄）。
    const domain = newCustDomain.value.trim() || undefined
    const c = auth.isReseller
      ? await reseller.createCustomer({ name, domain: domain ?? null })
      : await ls.createCustomer(name, domain)
    await store.loadCustomers()
    customerId.value = c.id
    custMode.value = 'existing'
    newCustName.value = ''
    newCustDomain.value = ''
    custError.value = ''
  } catch (e) {
    custError.value = errText(e, '建立客戶失敗')
  } finally {
    custBusy.value = false
  }
}

// --- ④ 套裝帶入 ---
async function applyPlan(key: string) {
  plan.value = key
  const p = plans.value.find((x) => x.key === key)
  if (!p) return
  if (Object.keys(p.modules).length) {
    modules.value = { ...p.modules }
    pickerKey.value++
  }
  // 批114：模板的相容數字欄併進配額包（只讀 `quotas` 會讓那些數字在套用套裝時消失）。
  quotas.value = seedQuotas(spec.value, p)
  quotaKey.value++
  // 批112 修正：試用套裝 ⇒ 證標 trial，期限用**代管**的 `hosted_trial_term_days`（30 天，
  // 與核准對話框同源），不是模板本身的 90 天（那是客戶自己機器的試用）。初值端點打不到
  // 才退回模板的 term_days。
  trial.value = p.kind === 'trial'
  if (p.kind === 'trial') {
    const tp = await hosting.hostedTrialPreset(productKey.value).catch(() => null)
    const days = tp?.term_days ?? p.term_days ?? 30
    termTotal.value = days * MINUTES_PER_DAY + (tp?.term_minutes ?? 0)
  }
}

// --- ⑤ 送出 ---
async function submit() {
  if (!pre.value?.ok || !hostId.value || !productKey.value) return
  // 批151：步驟指示器可以直接跳回第五步——內容閘在這裡再擋一次（admin 偏離＝必 403）。
  if (contentBlocked.value) return
  // 批114 裁示 ⑰：配額摘要用憑證設計的標籤組字（沒宣告配額的產品回空字串＝整段省略）。
  const quotaText = quotaSummaryText(spec.value, quotas.value)
  const secrets = secretsToSend.value
  const secretCount = secrets ? Object.keys(secrets).length : 0
  const ok = await dialogs.confirm({
    title: '確認開站',
    message: `即將在主機「${selectedHost.value?.name}」為客戶「${selectedCustomer.value?.name}」開一套「${productLabel(productKey.value)}」：${pre.value.domain}，並簽發一張${quotaText ? `${quotaText}、` : ''}${termLabel(termTotal.value)}的${trial.value ? '試用' : '正式'}授權${secretCount ? `，同時設定 ${secretCount} 項站台機密` : ''}。開站動作會實際安裝服務、建 DNS 與申請憑證。`,
    confirmText: '開始開站',
  })
  if (!ok) return
  submitting.value = true
  try {
    // 批151 C6：站台機密**只在這一次送出時**帶（preflight 一律不帶）。
    result.value = await hosting.provisionHostSite(hostId.value, {
      ...buildBody(),
      ...(secrets ? { site_secrets: secrets } : {}),
    })
    secretValues.value = {}
    resultOpen.value = true
  } catch (e) {
    await dialogs.notify({ title: '開站失敗', message: errText(e, '開站失敗（LS 端未留下半成品）') })
  } finally {
    submitting.value = false
  }
}

async function copySetupUrl() {
  const url = result.value?.setup_url
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
    copied.value = true
    copiedOnce.value = true
    window.setTimeout(() => { copied.value = false }, 2000)
  } catch {
    copied.value = false
  }
}

async function goJob() {
  // 批141：含一次性權杖的網址關掉就再也拿不到——沒複製過就先問一次。
  if (setupOneTime.value && !copiedOnce.value) {
    const ok = await dialogs.confirm({
      title: '還沒複製初始設定網址',
      message: '這個網址含一次性權杖，只會顯示這一次，關掉之後無法再查看。確定要關閉嗎？',
      confirmText: '仍要關閉',
      danger: true,
    })
    if (!ok) return
  }
  const jobId = result.value?.job.job_id
  resultOpen.value = false
  if (jobId) void router.push({ name: hr('job-detail'), params: { jobId } })
}

/** 確認總表的配額列（批114）：一項一列，標籤與順序全由憑證設計決定。 */
const quotaRows = computed(() => buildQuotaRows(spec.value, quotas.value, null))

const memProfile = computed(() => Object.entries(pre.value?.profile?.mem ?? {}))
const dnsText = computed(() => {
  const d = result.value?.dns
  if (!d) return '—'
  if (d.applied) return `已建立 A 記錄（${d.provider ?? '—'}）`
  return d.detail ?? '未自動建立，需人工處理'
})
</script>

<template>
  <div class="page">
    <div class="crumb">
      <RouterLink v-if="hostId" :to="{ name: hr('host-detail'), params: { id: hostId } }">← 回主機詳情</RouterLink>
      <RouterLink v-else :to="{ name: hr('hosts') }">← 代管主機</RouterLink>
    </div>

    <div class="page-head">
      <h1>開新站台</h1>
      <span class="spacer" />
      <span class="subtle">全部驗證通過才會落資料；任何一項不過就整批放棄，不留半成品。</span>
    </div>

    <!-- 步驟指示器 -->
    <ol class="wsteps">
      <li
        v-for="s in STEPS"
        :key="s.n"
        class="wstep"
        :class="{ on: step === s.n, done: s.n < step, clickable: s.n <= furthest }"
        @click="goto(s.n)"
      >
        <span class="wdot">{{ s.n }}</span>
        <span class="wlabel">{{ s.label }}</span>
      </li>
    </ol>

    <!-- ① 客戶 -->
    <div v-if="step === 1" class="card">
      <h3 class="section-title">這套站台是給哪個客戶？</h3>
      <div class="tabs cmode" data-hscroll>
        <button type="button" class="tab" :class="{ active: custMode === 'existing' }" @click="custMode = 'existing'">
          選既有客戶
        </button>
        <button type="button" class="tab" :class="{ active: custMode === 'new' }" @click="custMode = 'new'">
          建立新客戶
        </button>
      </div>

      <template v-if="custMode === 'existing'">
        <label class="field">
          <span>客戶</span>
          <select v-model="customerId">
            <option value="">— 請選擇 —</option>
            <option v-for="c in store.customers" :key="c.id" :value="c.id">
              {{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}
            </option>
          </select>
        </label>
        <p class="subtle">
          授權會簽給這個客戶，並自動代建一筆主機連結——客戶的 portal 立刻看得到這台。
        </p>
      </template>

      <template v-else>
        <div class="field-row two">
          <label class="field">
            <span>客戶名稱 *</span>
            <input v-model="newCustName" placeholder="宏遠實業" v-enter="createCustomer" />
          </label>
          <label class="field">
            <span>email 網域（選填）</span>
            <input v-model="newCustDomain" placeholder="acme.com" />
          </label>
        </div>
        <p class="subtle">填網域後，同網域的 Google email 首次登入 portal 會自動歸到此客戶。</p>
        <p v-if="custError" class="err">{{ custError }}</p>
        <div class="acts">
          <UiButton variant="primary" :loading="custBusy" :disabled="!newCustName.trim()" @click="createCustomer">
            建立並選用
          </UiButton>
        </div>
      </template>
    </div>

    <!-- ② 主機 -->
    <div v-if="step === 2" class="card">
      <div class="card-head">
        <h3 class="section-title">要開在哪一台主機？</h3>
        <span class="spacer" />
        <span class="subtle">客戶：{{ selectedCustomer?.name ?? '—' }}</span>
      </div>
      <div v-if="!hosts.length" class="empty">
        <div class="empty-sub">還沒有代管主機。請先到「代管主機」註冊一台並完成 agent enroll。</div>
      </div>
      <div v-else class="hostlist">
        <label
          v-for="r in hostRows"
          :key="r.host.id"
          class="hcard"
          :class="{ sel: hostId === r.host.id, blocked: r.blockers.length }"
        >
          <input v-model="hostId" type="radio" :value="r.host.id" :disabled="r.blockers.length > 0" />
          <div class="hbody">
            <div class="hline">
              <strong>{{ r.host.name }}</strong>
              <span class="status-pill" :class="r.host.status === 'active' ? 'active' : 'pending'">
                {{ hostStatusLabel(r.host.status) }}
              </span>
              <span v-if="r.host.exclusive" class="chip excl">
                獨佔{{ r.host.exclusive_customer_name ? `：${r.host.exclusive_customer_name}` : '（未指定客戶）' }}
              </span>
              <span class="hsub">{{ r.host.public_ip || r.host.fqdn || '未填 IP' }}</span>
              <span class="hsub">站數 {{ r.host.sites_count }} / {{ r.host.max_sites ?? '不限' }}</span>
            </div>
            <div class="hmeters">
              <UiMeter label="CPU" :pct="r.host.cpu_pct" />
              <UiMeter label="RAM" :pct="r.host.mem_used_pct" />
              <UiMeter label="磁碟" :pct="r.host.disk_used_pct_max" title="各掛載點最高水位" />
            </div>
            <ul v-if="r.blockers.length" class="blockers">
              <li v-for="b in r.blockers" :key="b">{{ b }}</li>
            </ul>
          </div>
        </label>
      </div>
      <p class="subtle">
        水位是 agent 最近一次心跳的快照；「每站需要多少記憶體／磁碟」的精算在第五步的開站前檢查。
      </p>
    </div>

    <!-- ③ 網域 -->
    <div v-if="step === 3" class="card">
      <div class="card-head">
        <h3 class="section-title">網域與站台選項</h3>
        <span class="spacer" />
        <span class="subtle">主機：{{ selectedHost?.name ?? '—' }}</span>
      </div>

      <label class="field">
        <span>站台代號（slug）*</span>
        <input v-model="slug" placeholder="acme（小寫英數與連字號、2–24 字）" />
      </label>
      <p v-if="slug && !slugOk" class="err">slug 只能是小寫英數與連字號、2–24 字且開頭為英數。</p>
      <p class="subtle">
        slug 決定站台目錄、compose project、systemd unit 名稱——<strong>開站後不可改</strong>。
        dev 環境會自動補 <code class="mono">-dev</code> 後綴。
      </p>

      <div class="tabs cmode dm" data-hscroll>
        <button type="button" class="tab" :class="{ active: domainMode === 'ours' }" @click="domainMode = 'ours'">
          我方子網域
        </button>
        <button type="button" class="tab" :class="{ active: domainMode === 'byo' }" @click="domainMode = 'byo'">
          客戶自帶網域
        </button>
      </div>

      <template v-if="domainMode === 'ours'">
        <!-- 批141-R：依 preflight 回的 DNS provider 說話——manual 不會自動建記錄。 -->
        <p v-if="dnsManual" class="subtle">
          這個產品的代管網域<template v-if="lastPre?.zone">（<code class="mono">{{ lastPre.zone }}</code>）</template>沒有接 DNS API（DNS provider＝manual）：開站<strong>不會自動建 A 記錄</strong>，要人工建立，生效後憑證才簽得下來。客戶要用自己的網域，之後再以「補簽憑證」附加第二域名（建議客戶用 CNAME 指向我方子網域）。
        </p>
        <!-- 批141-S：provider 不是 manual 也不保證會建——主機沒有對外 IP 時後端同樣不建（will_create_dns）。 -->
        <p v-else-if="dnsProvider && willCreateDns" class="subtle">
          這個網域的 DNS 由 <code class="mono">{{ dnsProvider }}</code> 管理：開站時自動建 A 記錄並直接申請憑證，不會卡在等客戶改 DNS。客戶要用自己的網域，之後再以
          「補簽憑證」附加第二域名（建議客戶用 CNAME 指向我方子網域，日後換機我方改 A 記錄即可）。
        </p>
        <p v-else-if="dnsProvider" class="subtle">
          這個網域的 DNS 由 <code class="mono">{{ dnsProvider }}</code> 管理，但主機尚未填對外 IP：開站<strong>不會自動建 A 記錄</strong>，要人工建立，生效後憑證才簽得下來（或先回主機頁補上對外 IP）。客戶要用自己的網域，之後再以「補簽憑證」附加第二域名（建議客戶用 CNAME 指向我方子網域）。
        </p>
        <p v-else class="subtle">
          網域落在這個產品的代管網域底下；會不會自動建 DNS 記錄依該網域的 DNS provider 而定，填好站台代號後會顯示。客戶要用自己的網域，之後再以「補簽憑證」附加第二域名（建議客戶用 CNAME 指向我方子網域）。
        </p>
      </template>
      <template v-else>
        <label class="field">
          <span>客戶網域 *</span>
          <input v-model="byoDomain" placeholder="app.acme.com" />
        </label>
        <p v-if="byoDomain && !byoOk" class="err">網域格式看起來不對（例：app.acme.com）。</p>
        <p class="warnbox">
          ⚠️ 自帶網域<strong>不會自動建 DNS</strong>：請客戶把
          <code class="mono">{{ byoDomain || '客戶網域' }}</code> 的 A 記錄指到
          <code class="mono">{{ selectedHost?.public_ip ?? '（主機尚未填對外 IP）' }}</code>，
          生效後憑證才簽得下來（開站流程會先跳過 TLS，之後用「補簽憑證」補上）。
        </p>
      </template>

      <!-- 即時 preflight：**依模式顯示對應的檢查結果**，其餘留到第五步總表。
           ★ 兩種模式看的東西完全不同：我方子網域看「這個 label 能不能用」，
           自帶網域看「客戶指過來了沒」——不可以共用同一套文案（會顯示我們根本不會
           建立的網域，並宣稱查過了 DNS zone）。 -->
      <div class="pre-inline">
        <span v-if="preBusy" class="subtle"><span class="spinner" /> 檢查中…</span>
        <template v-else-if="pre">
          <div class="mi">
            <span class="mk">站台代號<span class="hint">（compose 專案／資料目錄，非網址）</span></span>
            <span class="mv"><code class="mono">{{ pre.normalized_slug }}</code></span>
          </div>

          <!-- 我方子網域 -->
          <template v-if="domainMode === 'ours'">
            <div class="mi">
              <span class="mk">網域</span>
              <span class="mv"><code class="mono">{{ pre.domain }}</code></span>
            </div>
            <p v-if="pre.slug_error" class="err">{{ pre.slug_error }}</p>
            <template v-else>
              <!-- 批99-B：保留字（slug 黑名單）對**代理商強制**（外部角色一律 fail-closed），
                   對我方後台只是提醒。同一個 `slug_note` 兩種語氣，不要合成一句。 -->
              <p v-if="pre.slug_note && auth.isReseller" class="err">
                ⚠️ {{ pre.slug_note }}——保留字不開放代理商使用，請換一個站台代號
              </p>
              <p v-else-if="pre.slug_note" class="warnline">⚠️ {{ pre.slug_note }}——我方後台不擋，但這個名字容易被誤認成官方入口</p>
              <p v-else-if="dnsManual" class="okline">
                ✓ 站台代號可用（已查過既有站台；這個網域沒有接 DNS API，未比對 zone 的既有記錄）
              </p>
              <p v-else class="okline">✓ 站台代號可用（既有站台、DNS zone 都查過了）</p>
              <p v-if="dnsManual" class="warnline">
                ⚠️ 開站後需人工建立 A 記錄
                <code class="mono">{{ pre.domain }}</code> →
                <code class="mono">{{ pre.domain_check?.target_ip ?? '（主機尚未填對外 IP）' }}</code>——DNS provider 是 manual，不會自動建立；記錄生效後憑證才簽得下來。
              </p>
              <!-- 批141-S：「會建」與「需人工」分開寫——之前同一句先說會建、再補一句需要人工建立。 -->
              <p v-else-if="pre.domain_check?.will_create_dns" class="subtle">
                開站時會自動建 A 記錄
                <code class="mono">{{ pre.domain }}</code> →
                <code class="mono">{{ pre.domain_check?.target_ip }}</code>（DNS provider：{{ pre.dns_provider }}）
              </p>
              <p v-else class="warnline">
                ⚠️ 主機尚未填對外 IP：開站不會自動建 A 記錄
                <code class="mono">{{ pre.domain }}</code>，需要人工建立（指向這台主機的對外 IP），生效後憑證才簽得下來。
              </p>
            </template>
          </template>

          <!-- 客戶自帶網域 -->
          <template v-else>
            <div class="mi">
              <span class="mk">客戶網域</span>
              <span class="mv">
                <code class="mono">{{ pre.domain_check?.domain ?? '（尚未填寫）' }}</code>
              </span>
            </div>
            <div v-if="pre.domain_check?.format_ok" class="mi">
              <span class="mk">目前解析到</span>
              <span class="mv">
                <code v-if="pre.domain_check.resolved_ips?.length" class="mono">
                  {{ pre.domain_check.resolved_ips.join('、') }}
                </code>
                <span v-else class="subtle">查不到 A 記錄</span>
              </span>
            </div>
            <p v-if="pre.slug_error" class="err">{{ pre.slug_error }}</p>
            <p v-if="domainIssue" class="err">{{ pre.domain_check?.note }}</p>
            <p v-else-if="pre.domain_check?.points_here" class="okline">
              ✓ {{ pre.domain_check.note }}
            </p>
            <p v-else-if="pre.domain_check?.format_ok" class="warnline">
              ⚠️ {{ pre.domain_check.note }}
            </p>
            <p v-if="!pre.slug_error && pre.slug_note && auth.isReseller" class="err">
              ⚠️ {{ pre.slug_note }}——保留字不開放代理商使用，請換一個站台代號
            </p>
            <p v-else-if="!pre.slug_error && pre.slug_note" class="warnline">
              ⚠️ {{ pre.slug_note }}——我方後台不擋，但這個名字容易被誤認成官方入口
            </p>
            <p v-if="!pre.slug_error && pre.domain_check?.format_ok && !pre.domain_check?.in_use"
               class="subtle">
              站台代號已查過既有站台；<strong>不查我方 DNS zone</strong>——自帶網域
              不會在<template v-if="pre.zone"> <code class="mono">{{ pre.zone }}</code> </template><template v-else>我方的代管網域</template>建任何記錄。
            </p>
          </template>
        </template>
        <p v-else-if="preError" class="err">{{ preError }}</p>
        <!-- 批141：不擋開站的調整說明（例：產品不支援反向隧道、已略過）。 -->
        <p v-for="(n, i) in pre?.notes ?? []" :key="`n${i}`" class="subtle">ⓘ {{ n }}</p>
      </div>

      <h4 class="sub-title">站台選項</h4>
      <!-- 批141：只列這個產品的代管設定支援的選項（產品不支援的一律不畫、也不送）。 -->
      <p v-if="profileFeatures === undefined" class="subtle">
        填好站台代號後，會依產品的代管設定列出可用的站台選項。
      </p>
      <template v-else>
        <label v-if="featureOn('pgweb')" class="cb"><input v-model="pgwebEnabled" type="checkbox" /> 啟用 pgweb（唯讀資料庫檢視，另占一個 port）</label>
        <label v-if="featureOn('tunnel')" class="cb"><input v-model="tunnelEnabled" type="checkbox" /> 啟用反向隧道（客戶在防火牆後、需要 webhook 中繼時才開）</label>
        <p v-if="!featureOn('pgweb') && !featureOn('tunnel')" class="subtle">這個產品的站台沒有可選的站台選項。</p>
      </template>
    </div>

    <!-- ④ 授權 -->
    <div v-if="step === 4" class="card">
      <div class="card-head">
        <h3 class="section-title">授權內容</h3>
        <span class="spacer" />
        <span class="subtle">開站時直接簽出、寫進站台檔案（到期日以開站當下起算）</span>
      </div>

      <!-- 產品（批99-A）：候選只有一個＝事實，不做成只有一個選項的下拉（多一次點擊、
           還可能被誤認為「可以換」）。多於一個才給選，換產品會整包重帶模組與配額。 -->
      <label v-if="productCandidates.length > 1" class="field">
        <span>產品</span>
        <select v-model="productKey">
          <option v-for="k in productCandidates" :key="k" :value="k">{{ productLabel(k) }}</option>
        </select>
      </label>
      <div v-else class="mi">
        <span class="mk">產品</span>
        <span class="mv">
          <template v-if="productKey">{{ productLabel(productKey) }}</template>
          <!-- 批114 裁示 ㉟：「尚未回報」與「一個都不支援」是兩件事，指路不同。 -->
          <span v-else-if="hostNeedsAgentUpgrade" class="err">
            這台主機的 agent 尚未回報支援的產品，請先升級 agent
          </span>
          <span v-else class="err">這台主機沒有您可以開站的產品</span>
        </span>
      </div>

      <div class="plan-row">
        <span class="plan-label">套裝</span>
        <!-- 批99-B：代理商只能直開 `activation_policy === 'reseller'` 的套裝；其餘灰掉
             並說明去處（按得到卻必然 422 比灰掉更傷）。我方視角 `planDirectOk` 恆 true。 -->
        <button
          v-for="p in plans"
          :key="p.key"
          type="button"
          class="plan-chip"
          :class="{ on: plan === p.key, off: !planDirectOk(p.activation_policy) }"
          :disabled="!planDirectOk(p.activation_policy)"
          :title="planDirectOk(p.activation_policy) ? undefined : '需走申請流程（我方審核核發後自動派到站上）'"
          @click="applyPlan(p.key)"
        >
          {{ p.label }}
          <span v-if="!planDirectOk(p.activation_policy)" class="chip-note">需走申請流程</span>
        </button>
        <!-- 「客製」：代理商一律不可選（後端 422 `activation_policy_required`）；我方要營運主管以上
             （批151 開站內容閘：沒選套裝＝客製內容）。 -->
        <button
          type="button"
          class="plan-chip"
          :class="{ on: plan === '' && customPlanOk, off: !customPlanOk }"
          :disabled="!customPlanOk"
          :title="auth.isReseller ? '客製內容需走申請流程' : !customPlanOk ? '客製內容需「營運主管」以上' : undefined"
          @click="plan = ''; trial = false"
        >
          客製
          <span v-if="auth.isReseller" class="chip-note">需走申請流程</span>
          <span v-else-if="!customPlanOk" class="chip-note">需營運主管</span>
        </button>
      </div>
      <p v-if="auth.isReseller" class="subtle">
        直接開站只支援上面標示可用的套裝，且<strong>內容不可改動</strong>（模組、配額與期限
        必須與套裝模板一致）。要客製請到〈授權申請〉建草稿送出，我方審核核發後授權會自動派到站上。
      </p>
      <!-- 批151 開站內容閘：admin 偏離／客製＝送出必 403 → 這裡先講、並擋住下一步。
           營運主管以上只提示（他開得了，但要知道這是一份客製內容）。 -->
      <p v-if="contentHint" :class="contentBlocked ? 'warnbox' : 'subtle'">
        {{ contentBlocked ? '⚠️ ' : 'ⓘ ' }}{{ contentHint }}
      </p>

      <!-- 批114：獨立的數量欄已拔除（數量一律在下方「數量配額」填）。 -->
      <div class="form4">
        <label class="field">
          <span>期限</span>
          <!-- 批108：選項是**總分鐘**；下面再給一個自由輸入（代管站也允許分鐘級的證）。 -->
          <select v-model.number="termTotal">
            <option :value="30 * MINUTES_PER_DAY">1 個月</option>
            <option :value="90 * MINUTES_PER_DAY">3 個月</option>
            <option :value="365 * MINUTES_PER_DAY">1 年</option>
            <option :value="730 * MINUTES_PER_DAY">2 年</option>
          </select>
          <TermField v-model="termTotal" />
        </label>
      </div>

      <div class="mp">
        <ModulePicker :key="pickerKey" v-model="modules" :spec="spec" :allow-internal="auth.isManagerOrAbove" />
      </div>
      <h4 class="sub-title">數量配額</h4>
      <div class="mp">
        <QuotaEditor
          :key="quotaKey"
          v-model="quotas"
          :spec="spec"
          @update:valid="quotasValid = $event"
        />
      </div>

      <!-- 批151 C6：站台機密（產品有宣告＋營運主管以上才出現；全部選填，只在最後送出時帶）。 -->
      <template v-if="showSecrets">
        <h4 class="sub-title">站台機密</h4>
        <SiteSecretInputs v-model="secretValues" :fields="secretFields" :blocker="secretBlocker" />
      </template>
    </div>

    <!-- ⑤ 確認總表 -->
    <div v-if="step === 5" class="card">
      <div class="card-head">
        <h3 class="section-title">確認總表</h3>
        <span class="spacer" />
        <UiButton size="sm" :loading="preBusy" @click="runPreflight">重新檢查</UiButton>
      </div>

      <div v-if="preBusy && !pre" class="loading-wrap"><span class="spinner lg" /></div>
      <p v-else-if="preError" class="err">{{ preError }}</p>

      <template v-else-if="pre">
        <div v-if="pre.ok" class="okbox">✓ 開站前檢查全部通過</div>
        <div v-else class="blockbox">
          <strong>以下問題會讓開站被擋下</strong>
          <ul>
            <li v-for="i in pre.issues" :key="i">{{ i }}</li>
          </ul>
          <span class="subtle">請按上方步驟往回修正（或先處理主機／額度問題）後重新檢查。</span>
        </div>

        <div class="summary">
          <section>
            <h4>站台</h4>
            <div class="mi"><span class="mk">客戶</span><span class="mv">{{ selectedCustomer?.name ?? '—' }}</span></div>
            <div class="mi"><span class="mk">主機</span><span class="mv">{{ pre.host.name }}（{{ pre.host.public_ip ?? '無對外 IP' }}）</span></div>
            <div class="mi"><span class="mk">站台代號</span><span class="mv"><code class="mono">{{ pre.normalized_slug }}</code></span></div>
            <div class="mi">
              <span class="mk">網域</span>
              <span class="mv"><code class="mono">{{ pre.domain ?? '（尚未填寫）' }}</code></span>
            </div>
            <div class="mi">
              <span class="mk">網域來源</span>
              <span class="mv">
                <template v-if="domainMode === 'byo'">客戶自帶（需客戶自己指 A 記錄）</template>
                <template v-else-if="pre.dns_provider === 'manual'">我方子網域（需人工建 A 記錄，生效後簽憑證）</template>
                <template v-else>我方子網域（自動建 A 記錄＋簽憑證）</template>
              </span>
            </div>
            <div v-if="domainMode === 'byo'" class="mi">
              <span class="mk">DNS 現況</span>
              <span class="mv">
                <template v-if="pre.domain_check?.points_here">✓ 已指到這台主機，開站可直接簽憑證</template>
                <template v-else>⚠️ 尚未指到這台 → 開站先跳過 TLS，改好後用「補簽憑證」</template>
              </span>
            </div>
            <!-- DNS provider 只在我方子網域有意義：BYO 不經我們的 provider -->
            <div v-if="domainMode === 'ours'" class="mi">
              <span class="mk">DNS provider</span>
              <span class="mv">
                {{ pre.dns_provider }}
                <span v-if="pre.dns_provider === 'manual'" class="subtle">——不會自動建記錄，開站後請人工建立 A 記錄</span>
              </span>
            </div>
            <div class="mi">
              <span class="mk">出貨 channel</span>
              <span class="mv">
                <template v-if="pre.channel">
                  <code class="mono">{{ pre.channel }}</code>
                  <span v-if="isDevChannel(pre.channel)" class="subtle">
                    ——這是測試用的 channel，開出來的是測試站
                  </span>
                </template>
                <span v-else class="subtle">—（這個產品沒有有效的代管設定）</span>
              </span>
            </div>
            <div class="mi">
              <span class="mk">選項</span>
              <span class="mv">
                <template v-if="featureOn('pgweb') || featureOn('tunnel')">
                  <template v-if="featureOn('pgweb')">pgweb {{ pgwebEnabled ? '開' : '關' }}</template>
                  <template v-if="featureOn('pgweb') && featureOn('tunnel')">・</template>
                  <template v-if="featureOn('tunnel')">反向隧道 {{ tunnelEnabled ? '開' : '關' }}</template>
                </template>
                <span v-else class="subtle">—</span>
              </span>
            </div>
          </section>

          <section>
            <h4>主機資源</h4>
            <div class="mi">
              <span class="mk">站數</span>
              <span class="mv">{{ pre.host.sites_count }} / {{ pre.host.max_sites ?? '不限' }}（開完 {{ pre.host.sites_count + 1 }}）</span>
            </div>
            <div class="mi">
              <span class="mk">可用記憶體</span>
              <span class="mv">{{ pre.host.mem_free_mb == null ? '尚未回報' : `${Math.round(pre.host.mem_free_mb)} MB` }}</span>
            </div>
            <div class="mi">
              <span class="mk">資料碟可用</span>
              <span class="mv">{{ pre.host.disk_free_gb == null ? '尚未回報' : `${pre.host.disk_free_gb.toFixed(1)} GB` }}</span>
            </div>
            <div class="mi">
              <span class="mk">Let's Encrypt 本週</span>
              <span class="mv">{{ pre.le_weekly.used }} / {{ pre.le_weekly.budget }} 張</span>
            </div>
            <div class="mi">
              <span class="mk">記憶體 profile</span>
              <span class="mv chips">
                <span v-for="[k, v] in memProfile" :key="k" class="chip">{{ k }} {{ v }}</span>
              </span>
            </div>
          </section>

          <section>
            <h4>授權</h4>
            <div class="mi"><span class="mk">產品</span><span class="mv">{{ productKey ? productLabel(productKey) : '—' }}</span></div>
            <div class="mi"><span class="mk">套裝</span><span class="mv">{{ planLabel(spec, plan || null) }}</span></div>
            <div class="mi"><span class="mk">期限</span><span class="mv">{{ termLabel(termTotal) }}</span></div>
            <!-- 批114：兩行硬編的數量改成**全部配額**（一項一個 chip，這裡不必收成 +N）。 -->
            <div class="mi">
              <span class="mk">配額</span>
              <span class="mv chips">
                <span v-for="row in quotaRows" :key="row.key" class="chip">
                  {{ row.label }} {{ row.text }}
                </span>
                <span v-if="!quotaRows.length" class="subtle">—</span>
              </span>
            </div>
            <div class="mi">
              <span class="mk">模組</span>
              <span class="mv chips">
                <span v-for="(v, k) in modules" :key="k" class="chip">{{ k }}{{ v === '*' ? '' : `：${v}` }}</span>
              </span>
            </div>
            <!-- 批151 C6：只講幾項，不顯示值（也不顯示末四碼——開站成功後才在站台詳情看得到）。 -->
            <div v-if="showSecrets" class="mi">
              <span class="mk">站台機密</span>
              <span class="mv">
                <template v-if="secretsToSend">開站時設定 {{ Object.keys(secretsToSend).length }} 項（值不顯示）</template>
                <span v-else class="subtle">不帶（開站後可在站台詳情設定）</span>
              </span>
            </div>
            <div v-if="contentHint" class="mi">
              <span class="mk">開站內容</span>
              <span class="mv" :class="{ err: contentBlocked }">{{ contentHint }}</span>
            </div>
          </section>
        </div>

        <p v-for="(n, i) in pre.notes ?? []" :key="`sn${i}`" class="subtle">ⓘ {{ n }}</p>
        <p class="subtle setupnote">
          開站完成後會給一個初始設定網址——<strong>由客戶自己</strong>建立第一位管理員，
          我方不代建帳號。
        </p>
      </template>
    </div>

    <!-- 導覽列 -->
    <div class="navbar">
      <UiButton :disabled="step === 1" @click="prev">上一步</UiButton>
      <span class="spacer" />
      <span v-if="!canNext && step < 5" class="subtle">{{
        step === 1
          ? '請先選擇（或建立）客戶'
          : step === 2
            ? '請選一台可用的主機'
            : step === 3
              ? '請填合法的站台代號與網域'
              : hostNeedsAgentUpgrade
                ? '這台主機的 agent 尚未回報支援的產品，請先升級 agent'
                : !productKey
                  ? '這台主機沒有您可以開站的產品'
                  : !quotasValid
                    ? '「數量配額」還有必填項目沒填'
                    : contentBlocked
                      ? '內容偏離套裝（或未選套裝）需營運主管，請選套裝並照其內容開站'
                      : !secretsValid
                        ? '站台機密的長度必須介於 8～512 字'
                        : '期限至少要 1'
      }}</span>
      <span v-else-if="step === 5 && contentBlocked" class="subtle">內容偏離套裝需營運主管，請回第四步修正</span>
      <UiButton v-if="step < 5" variant="primary" :disabled="!canNext" @click="next">下一步</UiButton>
      <UiButton
        v-else
        variant="primary"
        :disabled="!pre?.ok || contentBlocked"
        :loading="submitting"
        @click="submit"
      >
        開始開站
      </UiButton>
    </div>

    <!-- 開站結果 -->
    <UiModal :open="resultOpen" title="開站已送出" width="640px" @close="goJob">
      <template v-if="result">
        <p class="okbox">
          站台 <strong>{{ result.site.slug }}</strong> 已登記、授權已簽出，安裝任務
          <code class="mono">{{ result.job.job_id }}</code> 已派給主機 agent。
        </p>
        <div class="mi"><span class="mk">網域</span><span class="mv"><code class="mono">{{ result.site.domain }}</code></span></div>
        <div class="mi"><span class="mk">授權編號</span><span class="mv"><code class="mono">{{ result.license_id }}</code></span></div>
        <div class="mi"><span class="mk">instance</span><span class="mv"><code class="mono">{{ result.site.instance_id }}</code></span></div>
        <div class="mi"><span class="mk">DNS</span><span class="mv">{{ dnsText }}</span></div>
        <p v-for="(n, i) in result.notes ?? []" :key="`rn${i}`" class="subtle">ⓘ {{ n }}</p>

        <div v-if="result.setup_url" class="field setup">
          <div class="field-label">交付給客戶的初始設定網址</div>
          <!-- 批141：含一次性權杖的網址只出現在這一次回應（之後的 API 回應一律遮罩權杖）——醒目提示先複製。 -->
          <p v-if="setupOneTime" class="onetime">
            ⚠️ 這個網址含<strong>一次性權杖</strong>，<strong>只會顯示這一次</strong>——關掉這個視窗後無法再查看，
            用過一次就失效。請現在就複製並交給客戶。
          </p>
          <pre class="cmd">{{ result.setup_url }}</pre>
          <p class="warnbox">
            這個網址要<strong>交給客戶自己開</strong>——第一位管理員由客戶在初始設定頁自行建立，
            我方不代建帳號。安裝任務跑完（狀態變「已完成」）之後才連得上。
          </p>
          <div class="acts">
            <UiButton size="sm" @click="copySetupUrl">{{ copied ? '已複製 ✓' : '複製網址' }}</UiButton>
          </div>
        </div>
      </template>
      <template #foot>
        <UiButton variant="primary" @click="goJob">前往任務進度</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.crumb {
  margin-bottom: var(--sp-3);
  font-size: var(--fs-12);
}
.crumb a {
  color: var(--fg-3);
  text-decoration: none;
}
.crumb a:hover {
  color: var(--fg);
}
.page-head {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.spacer {
  flex: 1;
}
.err {
  margin: var(--sp-2) 0 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
/* 批141：一次性權杖的初始設定網址（只顯示這一次）。 */
.onetime {
  margin: 0 0 var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(209, 67, 67, 0.45);
  border-radius: var(--r-sm);
  background: rgba(209, 67, 67, 0.08);
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}

/* 步驟指示器 */
.wsteps {
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0 0 var(--sp-4);
  padding: 0;
  gap: 0;
  flex-wrap: wrap;
}
.wstep {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: var(--sp-4);
  position: relative;
  font-size: var(--fs-12);
  color: var(--fg-4);
}
.wstep + .wstep {
  padding-left: var(--sp-4);
}
.wstep + .wstep::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: var(--sp-4);
  height: 1px;
  background: var(--border-strong);
}
.wstep.clickable {
  cursor: pointer;
}
.wdot {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--bg);
  font-size: var(--fs-12);
  font-weight: 700;
  color: var(--fg-4);
}
.wstep.done .wdot {
  background: var(--st-active-bg);
  border-color: transparent;
  color: var(--st-active-fg);
}
.wstep.on .wdot {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: var(--fg-inv);
}
.wstep.on .wlabel {
  color: var(--fg);
  font-weight: 700;
}

.cmode {
  margin-bottom: var(--sp-3);
}
.cmode.dm {
  margin-top: var(--sp-4);
}
.field-row.two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
.field + .subtle,
.subtle + .subtle {
  margin-top: var(--sp-2);
}
.acts {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}

/* ② 主機卡片 */
.hostlist {
  display: grid;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.hcard {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--bg);
  cursor: pointer;
}
.hcard:hover {
  border-color: var(--brand-400);
}
.hcard.sel {
  border-color: var(--brand-500);
  box-shadow: 0 0 0 3px var(--ring);
}
.hcard.blocked {
  opacity: 0.6;
  cursor: not-allowed;
}
.hcard input {
  margin-top: 3px;
}
.hbody {
  flex: 1;
  min-width: 0;
}
.hline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg);
}
.hsub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.hmeters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 2px var(--sp-4);
  margin-top: 6px;
  max-width: 620px;
}
.blockers {
  margin: 6px 0 0;
  padding-left: 1.2em;
  font-size: var(--fs-11);
  color: var(--danger);
}
.chip.excl {
  background: var(--st-transferred-bg);
  border-color: transparent;
  color: var(--st-transferred-fg);
  font-weight: 600;
}

/* ③ 網域 */
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--fg-3);
}
.cb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  padding: 3px 0;
}
.warnbox {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.pre-inline {
  margin-top: var(--sp-3);
  padding: var(--sp-3);
  border: 1px dashed var(--border-strong);
  border-radius: var(--r-md);
}
.okline {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-12);
  color: var(--success, var(--st-active-fg));
}
/* 自帶網域「還沒指過來」——不是錯誤（開站會先跳過 TLS），所以用警示色不用紅色 */
.warnline {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-12);
  color: var(--warning);
}
.mk .hint {
  margin-left: var(--sp-1);
  font-weight: 400;
  color: var(--fg-subtle);
}

/* ④ 授權 */
.plan-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.plan-label {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.plan-chip {
  padding: 4px 12px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fg-2);
  font-size: var(--fs-13);
  cursor: pointer;
}
/* 不可直開的套裝（批99-B）：灰掉但仍看得到——要讓人知道有這個套裝、只是得走申請。 */
.plan-chip.off {
  opacity: 0.55;
  cursor: not-allowed;
}
.chip-note {
  margin-left: 6px;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.plan-chip.on {
  border-color: var(--brand-500);
  background: var(--brand-500);
  color: #fff;
}
/* 批114：拔掉兩個數量欄之後只剩期限一欄。最小寬度 180 → 220px：`TermField` 是
   「數字＋單位」兩個控制項，180px 的格子會把它擠成兩行。 */
.form4 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--sp-3);
  margin-bottom: var(--sp-3);
}
.mp {
  margin: var(--sp-3) 0;
}

/* ⑤ 總表 */
.okbox {
  margin: 0 0 var(--sp-3);
  padding: 8px 12px;
  border-radius: var(--r-sm);
  background: var(--st-active-bg);
  color: var(--st-active-fg);
  font-size: var(--fs-12);
  font-weight: 600;
}
.blockbox {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border-radius: var(--r-md);
  background: rgba(209, 67, 67, 0.1);
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.blockbox ul {
  margin: 6px 0;
  padding-left: 1.2em;
}
.blockbox .subtle {
  color: var(--danger);
  opacity: 0.85;
}
.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--sp-4);
}
.summary h4 {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.mi {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  margin-bottom: 6px;
}
.mk {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.mv {
  font-size: var(--fs-13);
  color: var(--fg);
  overflow-wrap: anywhere;
}
.mv.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.setupnote {
  margin-top: var(--sp-4);
}

/* 導覽列 */
.navbar {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-4);
}
.cmd {
  margin: 0;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-mono);
  font-size: var(--fs-11);
  white-space: pre-wrap;
  word-break: break-all;
  user-select: all;
}
.setup {
  margin-top: var(--sp-4);
}
</style>
